import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  HostBinding,
  HostListener,
  Injector,
  OnInit,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { PanelModule } from 'primeng/panel';
import { PanelMenuModule } from 'primeng/panelmenu';
import { RippleModule } from 'primeng/ripple';
import {
  BehaviorSubject,
  combineLatest,
  delay,
  distinctUntilChanged,
  filter,
  map,
  merge,
  of,
  switchMap,
  timer,
} from 'rxjs';
import { IconKey } from '../../../../../whitelabel/icon-key.enum';
import { ButtonComponent } from '../../../../osp-ui/components/button/button/button.component';
import { PanelMenuComponent } from '../../../../osp-ui/components/menu/panel-menu/panel-menu.component';
import { TooltipDirective } from '../../../../osp-ui/components/overlay/tooltip/tooltip.directive';
import { AutomatedTransfersApiService } from '../../../../pages/transfers/automated-transfers/services/automated-transfers-api.service';
import { AutomatedTransfersCountsRefreshService } from '../../../../pages/transfers/automated-transfers/services/automated-transfers-counts-refresh.service';
import { Authorities } from '../../../../shared/enums/authorities.enum';
import { Modules } from '../../../../shared/enums/modules.enum';
import { AccessService } from '../../../../shared/services/access.service';
import { navigateToFirstNestedScreen } from '../../../../shared/utils/navigate-to-first-nested-screen';
import { IconProviderService } from '../../../../whitelabel/services/icon-provider.service';
import { ThemeService } from '../../../../whitelabel/services/theme.service';
import { AuthService } from '../../../services/auth.service';
import { LanguageService } from '../../../services/language.service';
import { SidebarItem } from './contract/sidebar-item.contract';
import { SidebarState } from './contract/sidebar-state.contract';
import { BaseSidebarStateDirective } from './directives/base-sidebar-state.directive';
import { LogoImagePipe } from './pipes/logo-image.pipe';

// TO DO: Create a base class for component, make corresponding changes for WNSidebarComponent
@Component({
  selector: 'osp-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    PanelModule,
    PanelMenuModule,
    PanelMenuComponent,
    LogoImagePipe,
    RippleModule,
    TranslateModule,
    ButtonComponent,
    TooltipDirective,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class SidebarComponent extends BaseSidebarStateDirective implements OnInit {
  @HostBinding('class.osp-sidebar') protected hostCssClassEnabled = true;
  @HostBinding('class') protected cssClassState: SidebarState = 'collapsed';

  private readonly accessService = inject(AccessService);
  private readonly isSuperOrPartnerAdmin = this.authService.isSuperOrPartnerAdmin();

  private readonly TRANSFERS_LIST_OPTION_ID = 'transferList';
  private readonly AUTOMATED_TRANSFERS_COUNT_INTERVAL_MS = 2000 * 60; // 2 minutes

  private readonly $canSeeAutomatedTransfers = signal(
    !this.isSuperOrPartnerAdmin &&
    this.authService.hasModule(Modules.AUTOMATED_TRANSFERS) &&
    this.authService.hasAuthorities([Authorities.ASSET_FROM_PROJECT_TRANSFER, Authorities.ASSET_FROM_STOCK_TRANSFER]),
  );
  private readonly canSeeLocations =
    !this.isSuperOrPartnerAdmin &&
    this.authService.hasAnyAuthority([
      Authorities.PROJECT_VIEW,
      Authorities.PROJECT_ASSIGNEE_VIEW,
      Authorities.STOCK_VIEW,
      Authorities.STOCK_MANAGE,
    ]) &&
    (this.authService.hasModule(Modules.DISPOSITION) || this.authService.hasModule(Modules.BULK_ITEM_MANAGEMENT));

  private readonly $automatedTransfersCounts = toSignal(
    merge(
      timer(0, this.AUTOMATED_TRANSFERS_COUNT_INTERVAL_MS),
      this.automatedTransfersCountsRefreshService.refresh$,
    ).pipe(
      switchMap(() =>
        this.$canSeeAutomatedTransfers() ? this.automatedTransfersApiService.getAutomatedTransfersCounts() : of(null),
      ),
    ),
    { injector: inject(Injector) },
  );

  private readonly $transfersListOption = computed<SidebarItem>(() => ({
    id: this.TRANSFERS_LIST_OPTION_ID,
    label: this.translate('modules.navigation.sidebar.transferList'),
    icon: this.iconProviderService.getIcon(IconKey.SidebarTransfers),
    expanded: false,
    visible: this.$canSeeAutomatedTransfers(),
    badgeValue: this.$automatedTransfersCounts()?.totalCount,
    items: [
      {
        label: this.translate('modules.navigation.sidebar.automatedTransfers.onTheRoad'),
        icon: this.iconProviderService.getIcon(IconKey.SidebarAutomatedTransfersOnTheRoad),
        routerLink: 'automated-transfers/on-the-road',
        routerLinkActiveOptions: { exact: false },
        visible: this.$canSeeAutomatedTransfers(),
        badgeValue: this.$automatedTransfersCounts()?.onTheRoadEquipmentsCount,
      },
      {
        label: this.translate('modules.navigation.sidebar.automatedTransfers.scheduled'),
        icon: this.iconProviderService.getIcon(IconKey.SidebarAutomatedTransfersScheduled),
        routerLink: 'automated-transfers/scheduled',
        routerLinkActiveOptions: { exact: false },
        visible: this.$canSeeAutomatedTransfers(),
        badgeValue: this.$automatedTransfersCounts()?.scheduledTransfersCount,
      },
    ],
    command: (event) => navigateToFirstNestedScreen(this.router, event.item),
  }));

  public $sidenavItems = signal<SidebarItem[]>([
    {
      label: this.translate('modules.navigation.sidebar.dashboard'),
      icon: this.iconProviderService.getIcon(IconKey.SidebarDashboard),
      routerLink: '/dashboard',
      visible: !this.isSuperOrPartnerAdmin,
      routerLinkActiveOptions: { exact: false },
    },
    {
      label: this.translate('modules.navigation.sidebar.myfleet'),
      icon: this.iconProviderService.getIcon(IconKey.SidebarMyFleet),
      expanded: false,
      visible: true,
      items: [
        {
          label: this.translate('modules.navigation.sidebar.equipments'),
          icon: this.iconProviderService.getIcon(IconKey.SidebarMyFleet),
          routerLink: 'resources/equipment',
          routerLinkActiveOptions: { exact: false },
          visible: this.authService.hasAuthority(Authorities.EQUIPMENT_VIEW),
        },
      ],
      command: (event) => navigateToFirstNestedScreen(this.router, event.item),
    },
    {
      label: this.translate('modules.navigation.sidebar.locations'),
      icon: this.iconProviderService.getIcon(IconKey.SidebarLocations),
      expanded: false,
      visible: this.canSeeLocations,
      items: [
        {
          label: this.translate('modules.navigation.sidebar.active'),
          icon: this.iconProviderService.getIcon(IconKey.SidebarLocationsActive),
          routerLink: 'locations/active',
          routerLinkActiveOptions: { exact: false },
          visible: true,
        },
        {
          label: this.translate('modules.navigation.sidebar.archived'),
          icon: this.iconProviderService.getIcon(IconKey.SidebarLocationsArchived),
          routerLink: 'locations/archived',
          routerLinkActiveOptions: { exact: false },
          visible: true,
        },
      ],
      command: (event) => navigateToFirstNestedScreen(this.router, event.item),
    },
    {
      label: this.translate('modules.navigation.sidebar.maintenance'),
      icon: this.iconProviderService.getIcon(IconKey.SidebarMaintenanceAndInspections),
      expanded: false,
      visible:
        this.authService.hasAnyAuthority([Authorities.MAINTENANCE_TASK_VIEW, Authorities.MAINTENANCE_TASK_MANAGE]) &&
        this.authService.hasModule(Modules.SERVICE),
      items: [
        {
          label: this.translate('modules.navigation.sidebar.maintenanceUpcoming'),
          icon: this.iconProviderService.getIcon(IconKey.SidebarMaintenanceAndInspectionsUpcoming),
          routerLink: 'maintenance/upcoming',
          visible: true,
        },
        {
          label: this.translate('modules.navigation.sidebar.maintenanceHistory'),
          icon: this.iconProviderService.getIcon(IconKey.SidebarMaintenanceAndInspectionsHistory),
          routerLink: 'maintenance/completed',
          visible: true,
        },
      ],
      command: (event) => navigateToFirstNestedScreen(this.router, event.item),
    },
    {
      label: this.translate('modules.navigation.sidebar.reports'),
      icon: this.iconProviderService.getIcon(IconKey.SidebarReporting),
      expanded: false,
      visible:
        !this.authService.isPartnerAdmin() &&
        this.authService.hasAnyAuthority([
          Authorities.REPORT_MANAGE,
          Authorities.REPORT_VIEW,
          Authorities.REPORT_NEXTGEN_READ,
          Authorities.REPORT_NEXTGEN_WRITE,
        ]),
      items: [
        {
          label: this.translate('modules.navigation.sidebar.reportsAvailable'),
          icon: this.iconProviderService.getIcon(IconKey.SidebarReportingAvailable),
          routerLink: 'reports/available',
          routerLinkActiveOptions: { exact: false },
          visible: true,
        },
        {
          label: this.translate('modules.navigation.sidebar.reportsAutomatic'),
          icon: this.iconProviderService.getIcon(IconKey.SidebarReportingAutomatic),
          routerLink: 'reports/automatic',
          routerLinkActiveOptions: { exact: false },
          visible: true,
        },
        {
          label: this.translate('modules.navigation.sidebar.reportsEmbedded'),
          icon: this.iconProviderService.getIcon(IconKey.SidebarReportingEmbedded),
          routerLink: 'reports/embedded',
          routerLinkActiveOptions: { exact: false },
          visible: this.authService.hasAnyAuthority([
            Authorities.REPORT_NEXTGEN_READ,
            Authorities.REPORT_NEXTGEN_WRITE,
          ]),
        },
      ],
      command: (event) => navigateToFirstNestedScreen(this.router, event.item),
    },
    { ...this.$transfersListOption() },
    {
      label: this.translate('modules.navigation.sidebar.alarms'),
      icon: this.iconProviderService.getIcon(IconKey.SidebarNotifications),
      routerLink: '/notifications',
      routerLinkActiveOptions: { exact: false },
      visible:
        this.authService.hasAnyAuthority([
          Authorities.NOTIFICATIONS_MANAGE_ALARMS,
          Authorities.NOTIFICATIONS_VIEW_ALARMS,
        ]) && this.authService.hasModule(Modules.ALARMS),
    },
    // {
    //   label: this.translate('modules.navigation.sidebar.analytics'),
    //   expanded: false,
    //   visible: true,
    //   items: [
    //     {
    //       label: this.translate('modules.analytics.base.transferlog'),
    //       routerLink: 'analytics/transfer-log',
    //       routerLinkActiveOptions: { exact: false },
    //       visible: true,
    //     },
    //   ],
    // },
    {
      label: this.translate('modules.navigation.sidebar.administration'),
      icon: this.iconProviderService.getIcon(IconKey.SidebarAdministration),
      expanded: false,
      visible: true,
      items: [
        {
          label: this.translate('modules.navigation.sidebar.userAdministration'),
          icon: this.iconProviderService.getIcon(IconKey.SidebarAdministrationManagement),
          routerLink: 'administration/user-list',
          routerLinkActiveOptions: { exact: false },
          visible: this.authService.hasAuthority(Authorities.USER_VIEW),
        },
        {
          label: this.translate('modules.navigation.sidebar.maintenance'),
          icon: this.iconProviderService.getIcon(IconKey.SidebarAdministrationMaintenanceAndInspections),
          routerLink: 'administration/maintenance',
          routerLinkActiveOptions: { exact: false },
          visible:
            this.authService.hasAuthorities([Authorities.MAINTENANCE_TASK_MANAGE]) &&
            this.authService.hasModule(Modules.SERVICE),
        },
        {
          label: this.translate('modules.navigation.sidebar.management'),
          icon: this.iconProviderService.getIcon(IconKey.SidebarAdministrationManagement),
          routerLink: 'administration/management',
          routerLinkActiveOptions: { exact: false },
          visible: this.authService.hasAnyAuthority([Authorities.CUSTOMER_VIEW, Authorities.ORGANISATION_VIEW]),
        },
        {
          label: this.translate('modules.navigation.sidebar.alarmSettings'),
          icon: this.iconProviderService.getIcon(IconKey.SidebarNotifications),
          routerLink: 'administration/alarmsettings',
          routerLinkActiveOptions: { exact: false },
          visible:
            this.authService.hasAuthority(Authorities.NOTIFICATIONS_MANAGE_DEFAULT_RECIPIENTS) &&
            this.authService.hasModule(Modules.ALARMS),
        },
        {
          label: this.translate('modules.navigation.sidebar.customization'),
          icon: this.iconProviderService.getIcon(IconKey.SidebarCustomization),
          routerLink: 'administration/customization',
          routerLinkActiveOptions: { exact: false },
          visible:
            this.authService.hasAnyAuthority([
              Authorities.EQUIPMENT_STATUS_MANAGE,
              Authorities.ADDITIONAL_FIELD_VIEW,
              Authorities.ADDITIONAL_FIELD_MANAGE,
            ]) || this.accessService.canViewEquipmentType(),
        },
      ],
      command: (event) => navigateToFirstNestedScreen(this.router, event.item),
    },
  ]);

  private readonly collapsingChangeDelayMs = 200;
  private readonly isFixed$ = new BehaviorSubject<boolean>(true);
  private readonly isCollapsed$ = new BehaviorSubject<boolean>(false);

  pinToggleIcon$ = this.isFixed$.asObservable().pipe(
    map((isFixed) => (isFixed ? IconKey.SidebarExpand : IconKey.SidebarCollapse)),
    map((iconKey) => this.iconProviderService.getIcon(iconKey)),
  );
  pinToggleTooltip$ = this.isFixed$
    .asObservable()
    .pipe(
      map((isFixed) =>
        this.translate(isFixed ? 'modules.navigation.sidebar.pinHint.unpin' : 'modules.navigation.sidebar.pinHint.pin'),
      ),
    );

  constructor(
    protected readonly themeService: ThemeService,
    private readonly languageService: LanguageService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly authService: AuthService,
    private readonly iconProviderService: IconProviderService,
    private readonly automatedTransfersApiService: AutomatedTransfersApiService,
    private readonly automatedTransfersCountsRefreshService: AutomatedTransfersCountsRefreshService,
    private readonly destroyRef: DestroyRef,
  ) {
    super();
    this.automatedTransfersCountsEffect();
  }

  ngOnInit(): void {
    this.initListenerSidebarState();
    this.subscribeToRouter();
    this.rerenderSidebar(); // removing items from sidebar if none of child items are visible
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    this.isCollapsed$.next(false);
  }
  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.isCollapsed$.next(true);
  }

  public toggleFixingState(): void {
    this.isFixed$.next(!this.isFixed$.getValue());
  }

  private initListenerSidebarState(): void {
    combineLatest([this.isFixed$, this.isCollapsed$.pipe(delay(this.collapsingChangeDelayMs))])
      .pipe(
        map(([isFixed, isCollapsed]) => this.stateResolver(isFixed, isCollapsed)),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((state) => {
        this.cssClassState = state;
        this.state.emit(state);
        this.cdr.markForCheck();
      });
  }

  private stateResolver(isFixed: boolean, isCollapsed: boolean): SidebarState {
    if (isFixed) {
      return 'expanded-fixed';
    }
    return isCollapsed ? 'collapsed' : 'expanded';
  }

  private checkActiveState(url: string): boolean {
    return this.router.url.startsWith('/' + url);
  }

  /* Necessary method because routerLinkActiveOptions property takes care of styling active item
  but does not expand its parent, when existent */
  private updateActiveItem(): void {
    this.$sidenavItems.update((sidenavItems) => {
      const updatedSidenavItems = [...sidenavItems];
      updatedSidenavItems.forEach((navItem) => {
        navItem.expanded = false;
      });
      updatedSidenavItems.forEach((navItem) => {
        this.expandActiveItems(navItem);
      });
      return updatedSidenavItems;
    });
  }

  private expandActiveItems(navItem: SidebarItem): boolean {
    if (navItem?.items) {
      // Recursively expand parent items if any child is active
      const hasActiveChild = navItem.items.some((subNavItem) => this.expandActiveItems(subNavItem));
      navItem.expanded = hasActiveChild;
      return hasActiveChild;
    }

    // Check for active state using partial match
    return this.checkActiveState(navItem.routerLink);
  }

  private subscribeToRouter(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.updateActiveItem();
      });
  }

  private rerenderSidebar(): void {
    this.$sidenavItems.update((sidenavItems) =>
      sidenavItems.map((item) => {
        if (item.items && item.items.length > 0) {
          return {
            ...item,
            visible: item.items.every(({ visible }) => !visible) ? false : item.visible,
          };
        }
        return item;
      }),
    );
  }

  protected translate(key: string): string {
    return this.languageService.getInstant(key);
  }

  private automatedTransfersCountsEffect(): void {
    effect(
      () => {
        this.$automatedTransfersCounts();
        this.$sidenavItems.update((values) => {
          return values.map((option) => {
            if (option.id === this.TRANSFERS_LIST_OPTION_ID) {
              return this.$transfersListOption();
            }
            return option;
          });
        });
        this.updateActiveItem();
      },
      { allowSignalWrites: true },
    );
  }
}
